import React from 'react';
import { AspectRatio, Card, Image, Box, IconButton, Icon, Text, Badge, HStack } from '@chakra-ui/react';
import {
  PencilIcon,
  TrashIcon,
  HeartIcon,
  DocumentDuplicateIcon,
  EllipsisVerticalIcon,
  ShareIcon,
  EyeIcon,
  EyeSlashIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { formatDistanceToNowStrict } from 'date-fns';
import { useParams } from 'react-router-dom';
import { Tooltip } from '@/components/ui/tooltip';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@/components/ui/menu';
import { Content, Favorite, FieldOption } from '@/lib/types';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

// `filterConfig.key` is a MeiliSearch facet path of the form
// "content.<referenceID>.value". On a search hit the same data is reachable via
// item.content[<referenceID>].value, so we strip the prefix/suffix to get the
// JS object key we actually need.
function refIdFromFacetKey(key: string): string {
  return key.replace(/^content\./, '').replace(/\.value$/, '');
}

function getPillsForItem(item: Content, filterConfig: FilterConfig[] | undefined): string[] {
  if (!item.content || !filterConfig?.length) return [];
  const itemContent = item.content as Record<string, { value?: unknown } | undefined>;
  const pills: string[] = [];
  for (const f of filterConfig) {
    const refId = refIdFromFacetKey(f.key);
    const raw = itemContent[refId]?.value;
    if (raw == null || raw === '') continue;
    const values = Array.isArray(raw) ? raw : [raw];
    for (const v of values) {
      const label = f.options.find((o) => o.option_value === v)?.option_label;
      if (!label) continue;
      pills.push(label);
      if (pills.length >= 2) return pills;
    }
  }
  return pills;
}

function formatAge(createdAt: string | undefined): string {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return `${formatDistanceToNowStrict(date, { addSuffix: false })} ago`
    .replace(' minutes', 'm')
    .replace(' minute', 'm')
    .replace(' hours', 'h')
    .replace(' hour', 'h')
    .replace(' days', 'd')
    .replace(' day', 'd')
    .replace(' months', 'mo')
    .replace(' month', 'mo')
    .replace(' years', 'y')
    .replace(' year', 'y');
}

type Props = {
  item: Content;
  favorites: { [key: number]: Favorite };
  filterConfig?: FilterConfig[];
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  onView: (item: Content) => void;
  onFavourite: (item: Content) => void;
  onGeneratePiblicLink: (item: Content) => void;
  onDuplicate: (item: Content) => void;
  onEdit: (item: Content) => void;
  onDelete: (item: Content) => void;
  onChangeStatus: (item: Content, status: 'draft' | 'published') => void;
  onPrint: (item: Content) => void;
  // Discovery surface — hide the overflow menu entirely. Favorite heart and Draft
  // badge stay so users can still pin items at a glance.
  compact?: boolean;
};

const ItemCardGrid: React.FC<Props> = ({
  item,
  favorites,
  filterConfig,
  lastContentElementRef,
  onView,
  onFavourite,
  onGeneratePiblicLink,
  onDuplicate,
  onEdit,
  onDelete,
  onChangeStatus,
  onPrint,
  compact = false,
}) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();
  const isFavourite = !!favorites[item.id];
  const canManage = !!(
    (isAdmin || isUser) &&
    workspaceId &&
    item.workspace_id === parseInt(workspaceId, 10)
  );
  const isDraft = item.status !== 'published';
  const age = formatAge(item.created_at);
  // DS ContentCard leads its tag row with the content type ("Drill", "Article"),
  // followed by field values ("Medium", "Tactical") when the surface has them.
  const typeName = item.content_type_name?.trim();
  const pills = [
    ...(typeName ? [typeName] : []),
    ...getPillsForItem(item, filterConfig),
  ];

  return (
    <Card.Root
      ref={lastContentElementRef}
      className='group'
      w='full'
      h='full'
      borderWidth={1}
      borderColor='border'
      borderRadius='lg'
      overflow='hidden'
      bg='bg.panel'
      shadow='sm'
      transition='transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease'
      _hover={{
        transform: 'translateY(-2px)',
        shadow: 'md',
        borderColor: 'border.emphasized',
      }}
    >
      <Box position='relative' cursor='pointer' onClick={() => onView(item)}>
        <AspectRatio ratio={16 / 9}>
          {item.thumbnail ? (
            <Image
              src={item.thumbnail}
              alt={item.name}
              loading='lazy'
              objectFit='cover'
              bg='bg.muted'
            />
          ) : (
            <Box
              bg='fg.strong'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <Text fontSize='xs' color='fg.muted'>
                No media available
              </Text>
            </Box>
          )}
        </AspectRatio>

        <HStack
          position='absolute'
          top={2}
          right={2}
          gap={2}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip content={isFavourite ? 'Remove from favorites' : 'Favorite'}>
            <IconButton
              aria-label='Favourite'
              size='xs'
              variant='solid'
              bg='whiteAlpha.900'
              color={isFavourite ? 'danger' : 'fg.muted'}
              shadow='sm'
              _hover={{ bg: 'white' }}
              onClick={() => onFavourite(item)}
            >
              <Icon as={isFavourite ? HeartSolidIcon : HeartIcon} />
            </IconButton>
          </Tooltip>

          {canManage && (
            <Tooltip content={isDraft ? 'Share to Workspace' : 'Hide from Workspace'}>
              <IconButton
                aria-label={isDraft ? 'Share to Workspace' : 'Hide from Workspace'}
                size='xs'
                variant='solid'
                bg='whiteAlpha.900'
                color='fg.muted'
                shadow='sm'
                _hover={{ bg: 'white' }}
                onClick={() => onChangeStatus(item, isDraft ? 'published' : 'draft')}
              >
                <Icon as={isDraft ? EyeSlashIcon : EyeIcon} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </Box>

      <Card.Body p={3} display='flex' flexDirection='column'>
        <Box flex='1' cursor='pointer' onClick={() => onView(item)}>
          <Card.Title
            fontFamily='heading'
            fontWeight='700'
            fontSize='h3'
            color='fg.strong'
            lineHeight={1.4}
            lineClamp={2}
            minH='2.8em'
          >
            {item.name}
          </Card.Title>

          {pills.length > 0 && (
            <HStack gap={1.5} mt={2} flexWrap='wrap'>
              {pills.map((label, i) => (
                <Badge key={`${label}-${i}`} size='xs'>
                  {label}
                </Badge>
              ))}
            </HStack>
          )}
        </Box>

        {/* DS ContentCard footer: divider-topped, pinned to the card bottom so
            footers align across a carousel/grid row. */}
        <HStack
          mt={2}
          pt='10px'
          borderTopWidth='1px'
          borderColor='border'
          justify='space-between'
          align='center'
        >
          <Text
            color='fg.muted'
            fontSize='xs'
            lineClamp={1}
            cursor='pointer'
            onClick={() => onView(item)}
            flex={1}
            minW={0}
          >
            {item.created_user && `${item.created_user}`}
            {item.created_user && age && ' · '}
            {age}
          </Text>

          {!compact && (
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton
                  aria-label='More actions'
                  size='xs'
                  variant='ghost'
                  color='fg.muted'
                  _hover={{ bg: 'bg.subtle', color: 'fg' }}
                >
                  <Icon as={EllipsisVerticalIcon} />
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                {canManage && (
                  <MenuItem value='edit' onClick={() => onEdit(item)}>
                    <Icon as={PencilIcon} w={4} h={4} />
                    Edit
                  </MenuItem>
                )}
                {canManage && (
                  <MenuItem value='duplicate' onClick={() => onDuplicate(item)}>
                    <Icon as={DocumentDuplicateIcon} w={4} h={4} />
                    Duplicate
                  </MenuItem>
                )}
                {(canManage || !!item.public_link) && (
                  <MenuItem value='share' onClick={() => onGeneratePiblicLink(item)}>
                    <Icon as={ShareIcon} w={4} h={4} />
                    Share
                  </MenuItem>
                )}
                <MenuItem value='print' onClick={() => onPrint(item)}>
                  <Icon as={PrinterIcon} w={4} h={4} />
                  Print
                </MenuItem>
                {canManage && (
                  <>
                    <MenuSeparator />
                    <MenuItem value='delete' onClick={() => onDelete(item)} color='danger'>
                      <Icon as={TrashIcon} w={4} h={4} />
                      Delete
                    </MenuItem>
                  </>
                )}
              </MenuContent>
            </MenuRoot>
          )}
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};

export default ItemCardGrid;
